import {
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    type PanResponderGestureState,
    type LayoutChangeEvent,
    PanResponder,
    View
} from "react-native";
import type {
    ColorSliderThumbKeyboardEventType
} from "./components/thumb/type";
import type IColorSliderProps from "./type";
import type {
    ColorSliderGestureHandlersType,
    ColorSliderDragType
} from "./type";
import stylesheet, {
    getColorSliderColors,
    useStyles
} from "./stylesheet";
import ColorSliderRamp from "./components/ramp";
import ColorSliderThumb from "./components/thumb";
import {
    getColorSliderLightestColor,
    getColorSliderDefaultValue,
    getColorSliderThumbColor,
    getColorSliderValueText,
    COLOR_SLIDER_CHROMA_MAX,
    getColorSliderMeasures,
    getColorSliderRange,
    getColorSliderColor,
    getColorSliderAlpha,
    getColorSliderStops
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    measurePointerRect,
    usePointerType
} from "../../helpers/pointer";
import {
    getSliderOffsetFraction,
    getSliderPointerOffset,
    getSliderKeyAction,
    getSliderFraction,
    snapSliderValue,
    moveSliderValue,
    getSliderOrigin
} from "../slider/util";
import {
    CONTROL_SIZES
} from "../../helpers/theme/control";
import Text from "../text";

const ColorSlider: FC<IColorSliderProps> = ({
    max: maxProp = COLOR_SLIDER_CHROMA_MAX,
    orientation = "horizontal",
    isChromaRelative = false,
    "aria-label": ariaLabel,
    accessibilityLabel,
    isDisabled = false,
    value: valueProp,
    formatValueText,
    size = "medium",
    customLocalize,
    initialValue,
    customTheme,
    isShowValue,
    onChangeEnd,
    hintText,
    onChange,
    channel,
    color,
    style,
    title,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const hintID = `${useId().replace(/[^a-zA-Z0-9]/g, "")}-hint`;

    const parsedColor = useMemo(() => getColorSliderColor(color), [
        color
    ]);

    const parsedAlpha = useMemo(() => getColorSliderAlpha(color), [
        color
    ]);

    const {
        max,
        min,
        step
    } = getColorSliderRange({
        chromaMax: maxProp,
        isChromaRelative,
        channel
    });

    const [
        innerValue,
        setInnerValue
    ] = useState<number>(initialValue ?? getColorSliderDefaultValue({
        color: parsedColor,
        alpha: parsedAlpha,
        isChromaRelative,
        channel
    }));

    const [
        isDragging,
        setIsDragging
    ] = useState<boolean>(false);

    const [
        length,
        setLength
    ] = useState<number>(0);

    const railRef = useRef<View>(null);

    const thumbRef = useRef<View>(null);

    const dragRef = useRef<ColorSliderDragType>({
        isChanged: false,
        isActive: false,
        origin: null,
        extent: 0
    });

    const value = Math.min(Math.max(valueProp ?? innerValue, min), max);

    const valueRef = useRef<number>(value);

    valueRef.current = value;

    const currentMeasures = getColorSliderMeasures({
        touchTargets,
        pointerType,
        iconSizes,
        spaces
    });

    const currentColors = getColorSliderColors({
        isDisabled,
        colors
    });

    const ringColor = getColorSliderLightestColor({
        second: colors.content.container.inverse,
        first: colors.content.container.default
    });

    const ramp = useMemo(() => getColorSliderStops({
        chromaMax: maxProp,
        color: parsedColor,
        isChromaRelative,
        channel
    }), [
        isChromaRelative,
        parsedColor,
        maxProp,
        channel
    ]);

    const {
        container: containerDynamicStyle,
        field: fieldDynamicStyle,
        track: trackDynamicStyle,
        head: headDynamicStyle,
        edge: edgeDynamicStyle
    } = useStyles({
        currentMeasures,
        controlHeights,
        currentColors,
        shapeConfigs,
        orientation,
        radiuses,
        borders,
        spaces
    });

    const labelText = title ?? localize(`colorSlider-${channel}`);

    const valueText = formatValueText ? formatValueText(value) : getColorSliderValueText({
        locale: activeLocale,
        isChromaRelative,
        localize,
        channel,
        value
    });

    const isValueShown = isShowValue ?? Boolean(title);

    const isField = Boolean(title) || isValueShown || Boolean(hintText);

    const labelVariant = CONTROL_SIZES[size].labelVariant;

    const fraction = getSliderFraction({
        value,
        min,
        max
    });

    const thumbOffset = Math.max(length - currentMeasures.thumbSize, 0) * fraction;

    const thumbFill = getColorSliderThumbColor({
        surface: colors.content.container.raised,
        color: parsedColor,
        isChromaRelative,
        channel,
        value
    });

    const handleCommit = (next: number): boolean => {
        if(next === valueRef.current) {
            return false;
        }

        valueRef.current = next;

        if(valueProp === undefined) {
            setInnerValue(next);
        }

        onChange?.(next);

        return true;
    };

    const getPointerValue = (offset: number, extent: number): number => {
        const offsetFraction = getSliderOffsetFraction({
            thumbSize: currentMeasures.thumbSize,
            length: extent,
            offset
        });

        return snapSliderValue({
            value: min + offsetFraction * (max - min),
            step,
            min,
            max
        });
    };

    const handleGrant = ({
        x0,
        y0
    }: PanResponderGestureState) => {
        if(isDisabled) {
            return;
        }

        dragRef.current = {
            isChanged: false,
            isActive: true,
            origin: null,
            extent: 0
        };

        measurePointerRect(railRef.current, ({
            height,
            pageX,
            width,
            pageY
        }) => {
            const extent = orientation === "vertical" ? height : width;

            const origin = getSliderOrigin({
                orientation,
                direction,
                height,
                pageX,
                pageY,
                width
            });

            const offset = getSliderPointerOffset({
                pointer: orientation === "vertical" ? y0 : x0,
                orientation,
                direction,
                origin
            });

            dragRef.current.origin = origin;

            dragRef.current.extent = extent;

            if(dragRef.current.isActive) {
                setIsDragging(true);

                thumbRef.current?.focus?.();
            }

            if(handleCommit(getPointerValue(offset, extent))) {
                dragRef.current.isChanged = true;
            }

            if(!dragRef.current.isActive) {
                onChangeEnd?.(valueRef.current);
            }
        });
    };

    const handleMove = ({
        moveX,
        moveY
    }: PanResponderGestureState) => {
        const {
            extent,
            origin
        } = dragRef.current;

        if(isDisabled || origin === null) {
            return;
        }

        const offset = getSliderPointerOffset({
            pointer: orientation === "vertical" ? moveY : moveX,
            orientation,
            direction,
            origin
        });

        if(handleCommit(getPointerValue(offset, extent))) {
            dragRef.current.isChanged = true;
        }
    };

    const handleRelease = () => {
        const wasActive = dragRef.current.isActive;

        const wasChanged = dragRef.current.isChanged;

        dragRef.current.isActive = false;

        setIsDragging(false);

        if(wasActive && (wasChanged || dragRef.current.origin !== null)) {
            onChangeEnd?.(valueRef.current);
        }
    };

    const handleThumbKeyDown = (event: ColorSliderThumbKeyboardEventType) => {
        if(isDisabled || event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        const action = getSliderKeyAction({
            shiftKey: event.shiftKey,
            key: event.key,
            direction
        });

        if(action === null) {
            return;
        }

        event.preventDefault();

        let nextValue = valueRef.current;

        if(action === "min") {
            nextValue = min;
        } else if(action === "max") {
            nextValue = max;
        } else {
            nextValue = moveSliderValue({
                value: valueRef.current,
                count: action,
                step,
                min,
                max
            });
        }

        if(handleCommit(nextValue)) {
            dragRef.current.isChanged = true;
        }
    };

    const handleThumbKeyUp = () => {
        if(!dragRef.current.isChanged || dragRef.current.isActive) {
            return;
        }

        dragRef.current.isChanged = false;

        onChangeEnd?.(valueRef.current);
    };

    const handleThumbStep = (count: 1 | -1) => {
        const changed = handleCommit(moveSliderValue({
            value: valueRef.current,
            count,
            step,
            min,
            max
        }));

        if(changed) {
            onChangeEnd?.(valueRef.current);
        }
    };

    const latestRef = useRef<ColorSliderGestureHandlersType>({
        handleRelease,
        handleGrant,
        isDisabled,
        handleMove
    });

    latestRef.current = {
        handleRelease,
        handleGrant,
        isDisabled,
        handleMove
    };

    const panResponder = useMemo(() => PanResponder.create({
        onPanResponderGrant: (_event, gestureState) => latestRef.current.handleGrant(gestureState),
        onPanResponderMove: (_event, gestureState) => latestRef.current.handleMove(gestureState),
        onStartShouldSetPanResponder: () => !latestRef.current.isDisabled,
        onMoveShouldSetPanResponder: () => !latestRef.current.isDisabled,
        onPanResponderTerminate: () => latestRef.current.handleRelease(),
        onPanResponderRelease: () => latestRef.current.handleRelease(),
        onPanResponderTerminationRequest: () => false
    }), []);

    const onRailLayout = ({
        nativeEvent
    }: LayoutChangeEvent) => {
        setLength(orientation === "vertical" ? nativeEvent.layout.height : nativeEvent.layout.width);
    };

    const renderRamp = () => {
        if(isDisabled) {
            return null;
        }

        return <ColorSliderRamp
            trackThickness={currentMeasures.trackThickness}
            thumbSize={currentMeasures.thumbSize}
            customTheme={customTheme}
            orientation={orientation}
            direction={direction}
            channel={channel}
            length={length}
            ramp={ramp}
        />;
    };

    const renderHead = () => {
        if(!title && !isValueShown) {
            return null;
        }

        return <View
            style={[
                stylesheet.head,
                headDynamicStyle
            ]}
        >
            {
                title ?
                    <Text
                        customColor={currentColors.labelColor}
                        customTheme={customTheme}
                        style={stylesheet.label}
                        variant={labelVariant}
                        numberOfLines={1}
                    >
                        {title}
                    </Text>
                :
                    null
            }
            {
                isValueShown ?
                    <Text
                        customColor={currentColors.valueColor}
                        customTheme={customTheme}
                        style={stylesheet.value}
                        variant={labelVariant}
                    >
                        {valueText}
                    </Text>
                :
                    null
            }
        </View>;
    };

    const renderHint = () => {
        if(!hintText) {
            return null;
        }

        return <Text
            customColor={currentColors.hintColor}
            customTheme={customTheme}
            variant="bodySmallSize"
            id={hintID}
        >
            {hintText}
        </Text>;
    };

    const renderRail = () => <View
        {...(isField ? {} : props)}
        {...panResponder.panHandlers}
        style={[
            isField ? null : style,
            stylesheet.container,
            orientation === "vertical" ? stylesheet.verticalRail : stylesheet.horizontalRail,
            isDisabled ? stylesheet.disabled : stylesheet.enabled,
            containerDynamicStyle
        ]}
        onLayout={onRailLayout}
        ref={railRef}
    >
        <View
            style={[
                stylesheet.track,
                orientation === "vertical" ? stylesheet.verticalTrack : stylesheet.horizontalTrack,
                trackDynamicStyle
            ]}
            aria-hidden={true}
        >
            {renderRamp()}
            <View
                style={[
                    stylesheet.edge,
                    edgeDynamicStyle
                ]}
            />
        </View>
        <ColorSliderThumb
            aria-label={ariaLabel ?? accessibilityLabel ?? labelText}
            aria-describedby={hintText ? hintID : undefined}
            currentMeasures={currentMeasures}
            onKeyDown={handleThumbKeyDown}
            onKeyUp={handleThumbKeyUp}
            customTheme={customTheme}
            orientation={orientation}
            pointerType={pointerType}
            onStep={handleThumbStep}
            isDisabled={isDisabled}
            isPressed={isDragging}
            currentColors={{
                ...currentColors,
                ringColor
            }}
            fillColor={thumbFill}
            valueText={valueText}
            offset={thumbOffset}
            viewRef={thumbRef}
            valueNow={value}
            valueMax={max}
            valueMin={min}
        />
    </View>;

    if(!isField) {
        return renderRail();
    }

    return <View
        {...props}
        style={[
            style,
            orientation === "vertical" ? stylesheet.verticalField : null,
            fieldDynamicStyle
        ]}
    >
        {renderHead()}
        {renderRail()}
        {renderHint()}
    </View>;
};
export default ColorSlider;
